import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { X, ChevronDown, Plus } from 'lucide-react';

/**
 * Reusable searchable tag selector for infra fields.
 * - Shows existing values as dropdown suggestions
 * - Allows typing a new custom value
 * - Single-select (returns a string)
 */
const InfraTagSearch = ({
    value,
    onChange,
    options = [],
    placeholder = 'Search or type new...',
    className = '',
    inputClassName = '',
}) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Filter options based on query
    const filtered = options.filter(opt =>
        opt.toLowerCase().includes(query.toLowerCase())
    );

    const showCreate = query.trim() &&
        !options.some(o => o.toLowerCase() === query.trim().toLowerCase());

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelect = (val) => {
        onChange(val);
        setQuery('');
        setOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setQuery('');
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {value ? (
                <div className="flex items-center gap-1.5 h-9 px-3 border border-gray-200 rounded-md bg-white">
                    <span className="text-sm text-gray-800 flex-1 truncate">{value}</span>
                    <button onClick={handleClear} className="text-gray-400 hover:text-gray-600 shrink-0">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <div className="relative">
                    <Input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder}
                        className={`h-9 text-sm pr-8 ${inputClassName}`}
                    />
                    <ChevronDown
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
                    />
                </div>
            )}

            {open && !value && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filtered.length === 0 && !showCreate && (
                        <p className="text-xs text-gray-400 p-3 text-center">No options yet</p>
                    )}
                    {filtered.map(opt => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => handleSelect(opt)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors truncate"
                        >
                            {opt}
                        </button>
                    ))}
                    {showCreate && (
                        <button
                            type="button"
                            onClick={() => handleSelect(query.trim())}
                            className="w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 transition-colors flex items-center gap-1.5 border-t border-gray-100"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Create "{query.trim()}"
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default InfraTagSearch;
